import { Button, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  EspdResponseDataType,
  type EspdResponseDto,
  type EspdRequirementResponseDto,
  type ResolvedEspdRequirementGroupDto,
} from "../../../../../autogen/bff-api";
import { EspdRequirementInput } from "./EspdRequirementInput";
import { espdAnswerFor, setEspdAnswer } from "./useEspdAnswers";

const ON_TRUE = "GROUP_FULFILLED.ON_TRUE";
const ON_FALSE = "GROUP_FULFILLED.ON_FALSE";

interface Props {
  group: ResolvedEspdRequirementGroupDto;
  espd: EspdResponseDto;
  onChange: (next: EspdResponseDto) => void;
  readOnly: boolean;
  parentAnswered?: boolean;
}

const hasEvidenceUrlRequirement = (group: ResolvedEspdRequirementGroupDto): boolean =>
  group.requirements.some((r) => r.responseDataType === EspdResponseDataType.EvidenceUrl) ||
  group.subGroups.some(hasEvidenceUrlRequirement);

const isElectronicEvidenceGroup = (group: ResolvedEspdRequirementGroupDto): boolean =>
  group.requirements.some((r) => r.responseDataType === EspdResponseDataType.Indicator) &&
  hasEvidenceUrlRequirement(group);

export const EspdRequirementGroupView = ({ group, espd, onChange, readOnly, parentAnswered = true }: Props) => {
  const { t } = useTranslation();
  const lead = group.requirements.find((r) => r.responseDataType === EspdResponseDataType.Indicator);
  const leadAnswer = lead ? espdAnswerFor(espd, lead.id)?.indicator : undefined;

  const update = (requirementId: string, patch: Partial<EspdRequirementResponseDto>) =>
    onChange(setEspdAnswer(espd, requirementId, patch));

  if (isElectronicEvidenceGroup(group)) {
    if (!parentAnswered) return null;
    if (leadAnswer !== true) {
      if (readOnly || !lead) return null;
      return (
        <Button variant="link" color="smPrimary" size="sm" alignSelf="flex-start" onClick={() => update(lead.id, { indicator: true })}>
          {t("Documentation available electronically? (provide link)")}
        </Button>
      );
    }

    return (
      <Stack spacing="3">
        <Text fontSize="sm" color="smMuted">
          {t("Documentation available electronically")}
        </Text>
        {group.subGroups.map((sub) => (
          <EspdRequirementGroupView
            key={sub.id}
            group={sub}
            espd={espd}
            onChange={onChange}
            readOnly={readOnly}
            parentAnswered
          />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing="4">
      {group.requirements.map((req) => (
        <EspdRequirementInput
          key={req.id}
          requirement={req}
          answer={espdAnswerFor(espd, req.id)}
          readOnly={readOnly}
          onChange={(patch) => update(req.id, patch)}
        />
      ))}
      {group.subGroups.map((sub) => {
        const active =
          sub.processingInstruction === ON_TRUE
            ? leadAnswer === true
            : sub.processingInstruction === ON_FALSE
              ? leadAnswer === false
              : true;
        if (!active) return null;
        return (
          <EspdRequirementGroupView
            key={sub.id}
            group={sub}
            espd={espd}
            onChange={onChange}
            readOnly={readOnly}
            parentAnswered={leadAnswer !== undefined}
          />
        );
      })}
    </Stack>
  );
};
